import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🔒 Tenant isolation: Enable credentials (cookies) for all requests
axios.defaults.withCredentials = true;

// 🔒 REQUEST INTERCEPTOR: Add Authorization header from localStorage token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 🔄 REFRESH TOKEN FLOW: Track if we're currently refreshing
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// 🔄 Add axios interceptor to handle 401 errors with automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = error.config?.url || '';
      
      // DON'T try to refresh on login/register failures
      const authEndpoints = ['/auth/login', '/auth/register', '/auth/2fa', '/auth/refresh'];
      const isAuthEndpoint = authEndpoints.some(endpoint => url.includes(endpoint));
      
      const optionalEndpoints = ['/lexy-ai/history', '/lexy-ai/query', '/liveavatar/'];
      const isOptionalEndpoint = optionalEndpoints.some(endpoint => url.includes(endpoint));
      
      if (isAuthEndpoint || isOptionalEndpoint) {
        return Promise.reject(error);
      }
      
      // 🔄 Try to refresh token
      if (isRefreshing) {
        // Queue this request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      console.log('🔄 Attempting token refresh...');
      
      try {
        // Call refresh endpoint - withCredentials ensures cookies are sent
        const response = await axios.post(`${API}/auth/refresh`, {}, {
          withCredentials: true
        });
        console.log('🔄 Token refreshed successfully');
        
        // Update token in localStorage
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          // Update the Authorization header for the retry
          originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
        }
        
        // Update user data if returned
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        
        isRefreshing = false;
        processQueue(null, null);
        
        // Retry original request with new token
        return axios(originalRequest);
      } catch (refreshError: any) {
        // Refresh failed - user must re-login
        console.log('🔄 Token refresh failed:', refreshError?.response?.data?.detail || refreshError.message);
        isRefreshing = false;
        processQueue(refreshError, null);
        
        // Only clear auth state if it's a genuine auth failure, not a network error
        if (refreshError?.response?.status === 401) {
          console.log('🔄 Clearing auth state - refresh token invalid/expired');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('is_sandbox');
          localStorage.removeItem('sandbox_tenant_id');
          
          // Emit custom event for React Router
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const auth = {
  async login(email, password) {
    console.log('🔒 auth.login() called with:', email);
    console.log('API endpoint:', `${API}/auth/login`);
    
    try {
      // CRITICAL: Clear any old sandbox flags before login
      localStorage.removeItem('is_sandbox');
      localStorage.removeItem('sandbox_tenant_id');
      
      const response = await axios.post(`${API}/auth/login`, { email, password });
      console.log('Login response:', response.data);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        console.log('🔐 2FA required for this account');
        return {
          requires_2fa: true,
          email: response.data.email,
          user_id: response.data.user_id
        };
      }
      
      const { user, token, is_sandbox, sandbox_tenant_id } = response.data;
      
      // Store user info and token (for cross-origin support)
      localStorage.setItem('user', JSON.stringify(user));
      if (token) {
        localStorage.setItem('token', token);
      }
      
      // Store sandbox status if user is in sandbox
      if (is_sandbox) {
        localStorage.setItem('is_sandbox', 'true');
        localStorage.setItem('sandbox_tenant_id', sandbox_tenant_id);
        console.log('✅ Sandbox status stored:', sandbox_tenant_id);
      }
      
      console.log('✅ User stored, token saved');
      return user;
    } catch (error) {
      console.error('❌ auth.login() ERROR:', error);
      throw error;
    }
  },
  
  async login2FA(email, password, code) {
    console.log('🔐 auth.login2FA() called');
    
    try {
      const response = await axios.post(`${API}/auth/login/2fa`, { 
        email, 
        password, 
        code 
      });
      
      const { user, token, is_sandbox, sandbox_tenant_id } = response.data;
      
      // Store user info and token
      localStorage.setItem('user', JSON.stringify(user));
      if (token) {
        localStorage.setItem('token', token);
      }
      
      // Store sandbox status if user is in sandbox
      if (is_sandbox) {
        localStorage.setItem('is_sandbox', 'true');
        localStorage.setItem('sandbox_tenant_id', sandbox_tenant_id);
      }
      
      console.log('✅ 2FA login successful');
      return user;
    } catch (error) {
      console.error('❌ auth.login2FA() ERROR:', error);
      throw error;
    }
  },
  
  async register(name, email, phone, password) {
    const response = await axios.post(`${API}/auth/register`, { 
      name, email, phone, password, role: 'USER' 
    });
    
    // 🔒 Tenant isolation: Token is now in HttpOnly cookie
    const { user, token, requires_2fa_setup } = response.data;
    localStorage.setItem('user', JSON.stringify(user));
    
    // Store token for cross-origin support
    if (token) {
      localStorage.setItem('token', token);
    }
    
    // Return with 2FA setup flag
    return {
      ...user,
      requires_2fa_setup: requires_2fa_setup || false
    };
  },
  
  async logout() {
    try {
      // Call backend to delete HttpOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('is_sandbox');
    localStorage.removeItem('sandbox_tenant_id');
    
    // Redirect to home
    window.location.href = '/';
  },
  
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
  
  saveUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('userUpdated', { detail: user }));
  },
  
  getToken() {
    // Return token from localStorage for cross-origin support
    return localStorage.getItem('token');
  },
  
  isAuthenticated() {
    // Check if user and token exists
    return !!this.getUser() && !!this.getToken();
  }
};
